import {observer} from "mobx-react";
import React from "react";
import i18n from "../../../i18n/i18n";
import {Popup} from "../../../common/components/shared/popup/popup";
import {TimetableActionModel, TimetableActionTypes} from "../../models/timetable";
import {TimetableService} from "../../services/timetableService";
import SimpleInfoElement from "../../../common/components/shared/simpleInfoElement/simpleInfoElement";
import "./detailsPopup.scss";
import {DateUtils} from "../../../helpers/dateUtils";
import {Api} from "../../../helpers/api";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import Row from "../../../common/components/shared/row/row";
import Loader from "../../../common/components/shared/loader/loader";

interface DetailsPopupProps {
    timetableElemId: string;
    openDetailsPopup: boolean;
    onCancel: () => void;
}

interface DetailsPopupState {
    timetableData: TimetableActionModel | undefined | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

class DetailsPopup extends React.Component<DetailsPopupProps, DetailsPopupState> {
    constructor(props: any) {
        super(props);

        this.state = {
            timetableData: undefined,
            response: undefined
        };
    }

    public async componentDidMount() {
        const response = await TimetableService.getAction(this.props.timetableElemId);
        this.setState({response});

        if (Api.isSuccess(response)) {
            this.setState({timetableData: response?.data || null});
        }
    }

    render() {
        return <Popup
            open={this.props.openDetailsPopup}
            className="details-popup"
            header={this.renderHeader()}
            content={this.renderContent()}
            cancelText={i18n.t("timetable.actions.detailsAction.cancelText")}
            cancel={this.props.onCancel}
            testId="details-popup"
            httpError={
                <>
                    {!Api.isSuccess(this.state.response) && (
                        <HttpError
                            response={this.state.response}
                            cause={i18n.t("timetable.actions.errors.errorMessage")}
                        />
                    )}
                </>
            }
        />;
    }

    private renderContent(): JSX.Element {
        if (this.state.timetableData) {
            return <div className="container">
                <Row class="flex-type">
                    <div className="column">
                        <SimpleInfoElement
                            label={i18n.t("timetable.headers.ferry")}
                            value={this.state.timetableData!.ferry?.name}
                            testId="ferry-test-id"/>
                        <SimpleInfoElement
                            label={i18n.t("timetable.headers.voyageNo")}
                            value={this.state.timetableData!.voyageNo}
                            testId="voyageNo-test-id"/>
                    </div>
                    <div className="column">
                        <SimpleInfoElement
                            label={i18n.t("timetable.headers.actionType")}
                            value={this.actionTypesToName(this.state.timetableData!.actionType)}
                            testId="action-type-test-id"/>
                        <SimpleInfoElement
                            label={i18n.t("timetable.headers.wharf")}
                            value={this.state.timetableData!.wharf?.name}
                            testId="wharf-test-id"/>
                    </div>
                </Row>
                <Row class="flex-type">
                    <SimpleInfoElement
                        label={i18n.t("timetable.headers.notes")}
                        value={this.state.timetableData!.notes}
                        testId="notes-test-id"/>
                </Row>
                <Row class="flex-type">
                    <div className="column">
                        <div className="scheduled-time">
                            <p className="scheduled-time-header">{i18n.t("timetable.headers.plannedTime")}</p>
                            <SimpleInfoElement
                                label={i18n.t("timetable.headers.arrivalTime")}
                                value={this.dateFormatted(this.state.timetableData?.arrivalTime)}
                                testId="arrival-time-test-id"/>
                            <SimpleInfoElement
                                label={i18n.t("timetable.headers.departureTime")}
                                value={this.dateFormatted(this.state.timetableData?.departureTime)}
                                testId="departure-time-test-id"/>
                        </div>
                    </div>
                    <div className="column">
                        <div className="actual-time">
                            <p className="actual-time-header">{i18n.t("timetable.headers.actualTime")}</p>
                            <SimpleInfoElement
                                label={i18n.t("timetable.headers.actualArrivalTime")}
                                value={this.state.timetableData?.actualArrivalTime != null ? this.dateFormatted(this.state.timetableData?.actualArrivalTime) : null}
                                testId="actual-arrival-time-test-id"
                            />
                            <SimpleInfoElement
                                label={i18n.t("timetable.headers.actualDepartureTime")}
                                value={this.state.timetableData?.actualDepartureTime != null ? this.dateFormatted(this.state.timetableData?.actualDepartureTime) : null}
                                testId="actual-departure-time-test-id"
                            />
                        </div>
                    </div>
                </Row>
            </div>;
        } else return <Loader />;
    }

    private renderHeader() {
        if (this.state.timetableData) {
            return <>{i18n.t("timetable.actions.detailsAction.header")}<strong>{this.state.timetableData!.ferry.name}</strong></>;
        } else return i18n.t("timetable.actions.detailsAction.header");
    }

    private actionTypesToName(action: TimetableActionTypes) {
        switch (action) {
            case TimetableActionTypes.ARRIVAL:
                return i18n.t("timetable.actions.names.arrival");
            case TimetableActionTypes.STOP:
                return i18n.t("timetable.actions.names.stop");
            case TimetableActionTypes.DEPARTURE:
                return i18n.t("timetable.actions.names.departure");
            default:
                return "";
        }
    }

    private dateFormatted(date: string | Date | undefined | null) {
        if (date) {
            return DateUtils.getFormattedDateWithTime(date);
        } else return "";
    }
}

export default observer(DetailsPopup);
